import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useMaterialGroupErrorStore, usePartsErrorStore } from '@/api/errors';
import { getMaterial } from '@/api/materials';
import { useMaterialGroupState } from '@/api/materialsGroup';
import {
  selectActions,
  selectHasMaterials,
  useCutlistState,
} from '@/api/store';
import { ConfirmationDialog } from '@/blocks/ConfirmationDialog';
import { Button } from '@/primitives/Button';
import { Alert, Icon, Layer, Sparkles, Trash } from '@/primitives/Icons';
import { useCurrentFeatures } from '@/theme';
import { getTrimConfigByType } from '@/utils/features';

import { MaterialDropdown } from './MaterialDropdown';
import styles from './MaterialGroupTabNav.module.css';

export const MaterialGroupTabNav = () => {
  const { beamProfiling } = useCurrentFeatures();
  const tab = useMaterialGroupState((state) => state.activeGroup);
  const setTab = useMaterialGroupState((state) => state.setActive);
  const { addGroup, removeGroup } = useMaterialGroupState();
  const groups = useMaterialGroupState((state) => state.groups);
  const hasMaterials = useCutlistState(selectHasMaterials);
  const { addPart } = useCutlistState(selectActions);
  const { materialGroupErrors, removeGroupErrors } =
    useMaterialGroupErrorStore();

  const select = (id: string) => () => setTab(id);
  const { t } = useTranslation();
  const { partErrors } = usePartsErrorStore();
  const [groupIdToRemove, setGroupIdToRemove] = useState('');
  const { supportedSheetEdgeTrims, defaultSheetEdgeTrimTypeForPartGroup } =
    useCurrentFeatures();

  const parts = useCutlistState((state) => state.parts);

  const hasBeamsGroup = groups.find((g) => g.type === 'beams');
  const showMaterialDropdown = beamProfiling && !hasBeamsGroup;

  const onRemoveGroup = (groupId: string) => {
    window.analytics.track('Cutlist Material Group Deleted', { groupId });
    removeGroupErrors(groupId);
    removeGroup(groupId);
  };

  const onAddSheetGroup = () => {
    window.analytics.track('Cutlist Material Group Added');

    const sheetEdgeTrimConfig = getTrimConfigByType(
      supportedSheetEdgeTrims,
      defaultSheetEdgeTrimTypeForPartGroup
    );
    addGroup({ sheetEdgeTrimConfig });
  };

  const onAddBeamGroup = () => {
    window.analytics.track('Cutlist Beams Group Added');

    const groupId = addGroup({ type: 'beams' });
    const newGroup = useMaterialGroupState
      .getState()
      .groups.find((g) => g.id === groupId);

    if (newGroup) {
      addPart(newGroup, 'beam');
    }
  };

  return (
    <section className={styles.tabNav}>
      {groups.map((g, index) => {
        const core1Material = getMaterial(g.core1);
        const coreName =
          g.sheetSizeSelection === 'automatic'
            ? `${core1Material?.variationGroup?.name || core1Material?.name} ${
                core1Material?.thicknessMM
              }mm`
            : core1Material?.name;
        const title = coreName || g.core1 || g.name;
        const partIds = parts
          .filter((part) => part.groupId === g.id)
          .map((p) => p.id);

        const partialCore = g.core1 && !core1Material;
        const pErrors = partErrors.some((p) => partIds.includes(p.partItemId));
        const errors = materialGroupErrors.some(
          (e) => e.materialGroupId === g.id
        );
        return (
          <div key={`tab-${g.id}`} className="flexAlign gap-xs">
            <Button
              role="tab"
              icon={
                partialCore || errors || pErrors ? (
                  <Icon icon={<Alert />} />
                ) : undefined
              }
              aria-selected={tab === g.id ? true : false}
              data-testid="tabButton"
              variant="tab"
              onClick={select(g.id)}
              className={tab === g.id ? styles.activeTab : styles.tab}
            >
              {g.aiSuggestions?.core1 && !Boolean(g.core1) && (
                <Icon icon={<Sparkles />} color="var(--blue-5)" />
              )}
              {`${index + 1}. `}
              {title || t('cutlist-form.empty')}
            </Button>
            <Button
              variant="icon"
              className="delete"
              style={{
                border: '1px solid var(--gray-5)',
              }}
              icon={<Icon icon={<Trash />} />}
              onClick={() => setGroupIdToRemove(g.id)}
            />
          </div>
        );
      })}

      {showMaterialDropdown ? (
        <MaterialDropdown
          onAddSheetGroup={onAddSheetGroup}
          onAddBeamGroup={onAddBeamGroup}
          hasMaterials={hasMaterials || false}
        />
      ) : (
        <Button
          disabled={!hasMaterials}
          name="add-group"
          icon={<Icon icon={<Layer />} />}
          onClick={() => {
            onAddSheetGroup();
          }}
          variant="secondaryPill"
        >
          {t('cutlist-form.cta.addGroup')}
        </Button>
      )}

      <ConfirmationDialog
        title={t('cutlist-form.confirmationDialog.title')}
        onConfirm={() => {
          onRemoveGroup(groupIdToRemove);
          setGroupIdToRemove('');
        }}
        onClose={() => setGroupIdToRemove('')}
        isOpen={!!groupIdToRemove}
        variant="alert"
      >
        {t('cutlist-form.confirmationDialog.content')}
      </ConfirmationDialog>
    </section>
  );
};
